import { Effect } from 'effect';
import { attemptStudio, attemptStudioPromise, type StudioRuntimeError } from '../effect-runtime';
import { withStudioPageCanvasEffect } from './page-canvas';
import { holdStudioPreviewCanvasEffect } from './page-preview';
import type { StudioContextValue } from './workspace';

export const STUDIO_THUMB_WIDTH = 112;
const waitVisibleEffect = Effect.fn('studio.thumbnail.visible')(function* (element: HTMLElement) {
  const owner = yield* Effect.acquireRelease(
    attemptStudio('pdfjs', 'thumbnail-observer', () => {
      const owner = {
        ready: undefined as (() => void) | undefined,
        observer: undefined as IntersectionObserver | undefined,
      };
      owner.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) owner.ready?.();
        },
        { rootMargin: '300px 0px' },
      );
      return owner;
    }),
    (owner) =>
      attemptStudio('pdfjs', 'thumbnail-unobserve', () => {
        owner.ready = undefined;
        owner.observer?.disconnect();
      }).pipe(Effect.ignore),
  );
  yield* Effect.callback<void, StudioRuntimeError>((resume) => {
    owner.ready = () => resume(Effect.void);
    try {
      owner.observer!.observe(element);
    } catch (cause) {
      resume(
        attemptStudio('pdfjs', 'thumbnail-observe', () => {
          throw cause;
        }),
      );
    }
  });
}, Effect.scoped);

export const holdRailThumbnailEffect = Effect.fn('studio.thumbnail.hold')(function* (
  renderer: Pick<StudioContextValue, 'getPageSize' | 'renderPage'>,
  page: number,
  host: HTMLDivElement,
  onHeight: (height: number) => void,
) {
  yield* waitVisibleEffect(host);
  const size = yield* attemptStudioPromise('pdfjs', 'thumbnail-size', () =>
    renderer.getPageSize(page),
  );
  const scale = STUDIO_THUMB_WIDTH / size.width;
  yield* attemptStudio('pdfjs', 'thumbnail-height', () =>
    onHeight(Math.round(size.height * scale)),
  );
  return yield* withStudioPageCanvasEffect(renderer.renderPage, page, scale, (canvas) =>
    holdStudioPreviewCanvasEffect(host, canvas, () => {}, {
      className: '',
      width: `${STUDIO_THUMB_WIDTH}px`,
    }),
  );
});
